import React from "react";
import {
  ArrowRight,
  CalendarDays,
  ChartGantt,
  CheckCircle2,
  ClipboardList,
  FileText,
  KanbanSquare,
  SkipForward,
  Target,
} from "lucide-react";
import { projectService } from "@/features/projects/services/projectService";
import { useSettings } from "@/features/settings/contexts/SettingsContext";
import { getCurrentLocale } from "@/shared/i18n/config";
import type { ProjectData } from "@/shared/types/projectData";
import { Button } from "@/ui/primitives/Button";
import { cn, useTheme } from "@/ui/theme/ThemeContext";

type PreferredStartView = "gantt" | "kanban" | "docs";

interface SelfServiceSetupWizardProps {
  project: ProjectData;
  onComplete: (preferredStartView: PreferredStartView) => void | Promise<void>;
  onSkip: () => void | Promise<void>;
}

// These seed a persisted project, so they must never depend on the current UI language.
const DEFAULT_TASKS = [
  "Define the launch outcome",
  "Map the first launch work",
  "Prepare the first launch report",
];

const formatDefaultLaunchWindowEnd = (): string => {
  const fallbackEnd = new Date();
  fallbackEnd.setDate(fallbackEnd.getDate() + 14);
  // Follow the app language like every other date, not the browser locale.
  return new Intl.DateTimeFormat(getCurrentLocale(), { month: "short", day: "numeric" }).format(fallbackEnd);
};

const getStartViewOptions = (t: (key: string) => string): Array<{
  id: PreferredStartView;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}> => [
  {
    id: "gantt",
    label: t("Gantt"),
    description: t("Timeline first"),
    icon: ChartGantt,
  },
  {
    id: "kanban",
    label: t("Kanban"),
    description: t("Board first"),
    icon: KanbanSquare,
  },
  {
    id: "docs",
    label: t("Docs"),
    description: t("Notes first"),
    icon: FileText,
  },
];

const taskTitlesFromProject = (project: ProjectData): string[] => {
  const titles = (project.nodes || [])
    .filter((node) => node.type === "task")
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .slice(0, 3)
    .map((node) => String(node.title || "").trim())
    .filter(Boolean);

  return DEFAULT_TASKS.map((fallback, index) => titles[index] || fallback);
};

export const SelfServiceSetupWizard: React.FC<SelfServiceSetupWizardProps> = ({
  project,
  onComplete,
  onSkip,
}) => {
  const { t } = useSettings();
  const { theme } = useTheme();
  const startViewOptions = React.useMemo(() => getStartViewOptions(t), [t]);
  const [step, setStep] = React.useState(1);
  const [workspaceName, setWorkspaceName] = React.useState(project.projectName || "My Workspace");
  const [outcome, setOutcome] = React.useState("");
  const [targetDate, setTargetDate] = React.useState("");
  const [taskTitles, setTaskTitles] = React.useState<string[]>(() => taskTitlesFromProject(project));
  const [preferredStartView, setPreferredStartView] = React.useState<PreferredStartView>("gantt");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const fallbackWindowEnd = formatDefaultLaunchWindowEnd();

  const hasProjectBasics = workspaceName.trim().length > 0 && outcome.trim().length > 0;
  const hasTaskTitles = taskTitles.every((title) => title.trim().length > 0);
  const canContinue = step === 1 ? hasProjectBasics : step === 2 ? hasTaskTitles : true;

  const updateTaskTitle = (index: number, value: string) => {
    setTaskTitles((current) => {
      const next = [...current];
      next[index] = value;
      return next;
    });
  };

  const handleComplete = async () => {
    if (!hasProjectBasics || !hasTaskTitles) {
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await projectService.completeSelfServiceOnboarding(project.projectId, {
        workspaceName: workspaceName.trim(),
        outcome: outcome.trim(),
        targetDate: targetDate || null,
        firstTaskTitles: taskTitles.map((title) => title.trim()),
        preferredStartView,
      });
      await onComplete(preferredStartView);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Workspace setup failed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await projectService.skipSelfServiceOnboarding(project.projectId);
      await onSkip();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Workspace setup failed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrimaryAction = () => {
    if (step < 3) {
      setStep((current) => current + 1);
      return;
    }
    void handleComplete();
  };

  const handleStepSelect = (targetStep: number) => {
    // A later step must not make the final action forget required earlier fields.
    if (
      targetStep > step && (
        (targetStep >= 2 && !hasProjectBasics) ||
        (targetStep >= 3 && !hasTaskTitles)
      )
    ) {
      return;
    }
    setStep(targetStep);
  };

  return (
    <main
      data-testid="self-service-setup-wizard"
      className={cn("min-h-screen px-4 py-8 transition-colors sm:px-6 lg:px-8", theme.bgPrimary)}
    >
      <section className="mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-5xl items-center">
        <div className={cn("w-full rounded-lg border p-5 shadow-xl sm:p-7", theme.bgSecondary, theme.borderPrimary)}>
          <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-md", theme.accentBlue)}>
                <ClipboardList className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <p className={cn("text-xs font-semibold uppercase tracking-[0.12em]", theme.infoState)}>
                  {t("Step {{current}} of {{total}}", { current: step, total: 3 })}
                </p>
                <h1 className={cn("mt-1 text-xl font-semibold sm:text-2xl", theme.textPrimary)}>
                  {t("Build your launch plan")}
                </h1>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              loading={isSubmitting}
              leftIcon={<SkipForward className="h-4 w-4" />}
            >
              {t("Skip setup")}
            </Button>
          </div>

          <div className="mt-6 grid gap-6 lg:grid-cols-[0.72fr_1fr]">
            <aside className="min-w-0">
              <p className={cn("text-sm leading-6", theme.textSecondary)}>
                {t("Turn a messy project into a usable launch plan in minutes. Name the workspace, keep the first tasks sharp, then start where you work best.")}
              </p>
              <div className="mt-5 space-y-2">
                {[
                  ["Project basics", "Name the workspace and define the outcome."],
                  ["First tasks", "Keep the opening checklist short and actionable."],
                  ["Start view", "Open the view that gets you moving."],
                ].map(([title, description], index) => {
                  const itemStep = index + 1;
                  const isActive = itemStep === step;
                  const isComplete = itemStep < step;
                  return (
                    <button
                      key={title}
                      type="button"
                      onClick={() => handleStepSelect(itemStep)}
                      className={cn(
                        "flex w-full min-w-0 items-start gap-3 rounded-lg border p-3 text-left transition",
                        isActive ? theme.surfaceSelected : cn(theme.borderPrimary, theme.surfaceHover),
                      )}
                    >
                      <span
                        className={cn(
                          "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-semibold",
                          isComplete ? theme.accentGreen : isActive ? theme.accentBlue : theme.bgTertiary,
                        )}
                      >
                        {isComplete ? <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> : itemStep}
                      </span>
                      <span className="min-w-0">
                        <span className={cn("block text-sm font-semibold", theme.textPrimary)}>{t(title)}</span>
                        <span className={cn("mt-0.5 block text-xs leading-5", theme.textMuted)}>{t(description)}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </aside>

            <div className="min-w-0">
              {step === 1 ? (
                <div className="space-y-4">
                  <div>
                    <label className={cn("text-sm font-medium", theme.textPrimary)} htmlFor="workspace-name">
                      {t("Launch plan name")}
                    </label>
                    <input
                      id="workspace-name"
                      value={workspaceName}
                      onChange={(event) => setWorkspaceName(event.target.value)}
                      className={cn("mt-2 h-11 w-full rounded-md border px-3 text-sm outline-none", theme.bgTertiary, theme.borderPrimary, theme.textPrimary, theme.focusRing)}
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <label className={cn("text-sm font-medium", theme.textPrimary)} htmlFor="workspace-outcome">
                      {t("What pain should this plan end?")}
                    </label>
                    <textarea
                      id="workspace-outcome"
                      value={outcome}
                      onChange={(event) => setOutcome(event.target.value)}
                      className={cn("mt-2 min-h-32 w-full resize-none rounded-md border px-3 py-3 text-sm leading-6 outline-none", theme.bgTertiary, theme.borderPrimary, theme.textPrimary, theme.focusRing)}
                      maxLength={1000}
                      placeholder={t("Example: Ship the first customer-ready release without losing tasks, dates, or decisions.")}
                    />
                  </div>
                </div>
              ) : null}

              {step === 2 ? (
                <div className="space-y-4">
                  <div>
                    <label className={cn("text-sm font-medium", theme.textPrimary)} htmlFor="target-date">
                      {t("Target date")}
                    </label>
                    <div className="relative mt-2">
                      <CalendarDays className={cn("pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2", theme.textMuted)} aria-hidden="true" />
                      <input
                        id="target-date"
                        type="date"
                        value={targetDate}
                        onChange={(event) => setTargetDate(event.target.value)}
                        className={cn("h-11 w-full rounded-md border pl-10 pr-3 text-sm outline-none", theme.bgTertiary, theme.borderPrimary, theme.textPrimary, theme.focusRing)}
                      />
                    </div>
                    <p className={cn("mt-2 text-xs leading-5", theme.textMuted)}>
                      {t("Leave it blank and Management Tool will schedule the first launch window through {{date}}.", { date: fallbackWindowEnd })}
                    </p>
                  </div>
                  <div className="space-y-3">
                    {taskTitles.map((title, index) => (
                      <div key={index}>
                        <label className={cn("text-sm font-medium", theme.textPrimary)} htmlFor={`starter-task-${index}`}>
                          {t('Task {{number}}', { number: index + 1 })}
                        </label>
                        <div className="relative mt-2">
                          <Target className={cn("pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2", theme.textMuted)} aria-hidden="true" />
                          <input
                            id={`starter-task-${index}`}
                            value={title}
                            onChange={(event) => updateTaskTitle(index, event.target.value)}
                            className={cn("h-11 w-full rounded-md border pl-10 pr-3 text-sm outline-none", theme.bgTertiary, theme.borderPrimary, theme.textPrimary, theme.focusRing)}
                            maxLength={200}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {step === 3 ? (
                <div className="space-y-4">
                  <div>
                    <h2 className={cn("text-base font-semibold", theme.textPrimary)}>{t("Choose where to start")}</h2>
                    <p className={cn("mt-1 text-sm", theme.textSecondary)}>
                      {t("Gantt is the default because launch plans usually need dates first.")}
                    </p>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    {startViewOptions.map((option) => {
                      const Icon = option.icon;
                      const selected = preferredStartView === option.id;
                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => setPreferredStartView(option.id)}
                          className={cn(
                            "min-h-32 rounded-lg border p-4 text-left transition",
                            selected ? theme.surfaceSelected : cn(theme.borderPrimary, theme.surfaceHover),
                          )}
                        >
                          <Icon className={cn("h-5 w-5", selected ? "" : theme.textSecondary)} aria-hidden="true" />
                          <span className={cn("mt-4 block text-sm font-semibold", theme.textPrimary)}>{option.label}</span>
                          <span className={cn("mt-1 block text-xs", theme.textMuted)}>{option.description}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {error ? (
                <div className={cn("mt-5 rounded-md border px-3 py-2 text-sm", theme.accentRed)}>
                  {error}
                </div>
              ) : null}

              <div className="mt-6 flex min-w-0 flex-wrap items-center justify-between gap-3 border-t pt-4 dark:border-gray-700">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setStep((current) => Math.max(1, current - 1))}
                  disabled={step === 1 || isSubmitting}
                >
                  {t("Back")}
                </Button>
                <Button
                  type="button"
                  size="md"
                  onClick={handlePrimaryAction}
                  loading={isSubmitting}
                  disabled={!canContinue}
                  rightIcon={step < 3 ? <ArrowRight className="h-4 w-4" /> : <ChartGantt className="h-4 w-4" />}
                >
                  {step < 3 ? t("Continue") : t("Enter workspace")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
